import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../services/auth';
import { Badge } from './design';

export default function UserDropdown({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const nav = (hash) => {
    setOpen(false);
    window.location.hash = hash;
  };

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 cursor-pointer"
        style={{ padding: '4px 8px', borderRadius: 10 }}
      >
        <div className="text-right hidden sm:block">
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>
            {user?.full_name || user?.username}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {user?.role?.replace('_', ' ')}
          </div>
        </div>
        <div className="flex items-center justify-center" style={{ width: 32, height: 32, background: 'var(--grad-main)', borderRadius: '50%', color: '#fff', fontWeight: 500, fontSize: 14 }}>
          {(user?.full_name || user?.username || 'A').charAt(0).toUpperCase()}
        </div>
        <svg
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
          style={{ width: 14, height: 14, color: 'var(--text-3)' }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && (
        <div className="absolute right-0 z-50 overflow-hidden" style={{ marginTop: 8, width: 288, background: 'var(--surface)', borderRadius: 12, boxShadow: 'var(--shadow)', border: '1px solid var(--glass-border)' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--glass-border)' }}>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--grad-main)', borderRadius: '50%', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                {(user?.full_name || user?.username || 'A').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>
                  {user?.full_name || user?.username}
                </div>
                <div className="truncate" style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {user?.email}
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '4px 0' }}>
            <DropdownItem
              icon={UserIcon}
              label="Profile"
              onClick={() => nav('profile')}
            />

            <DropdownSection label="Security">
              <DropdownItem
                icon={KeyIcon}
                label="Change Password"
                indent
                onClick={() => nav('user-security')}
              />
              <DropdownItem
                icon={ShieldIcon}
                label="Two-Factor Auth"
                badge="Disabled"
                indent
                onClick={() => nav('user-security')}
              />
            </DropdownSection>

            <DropdownItem
              icon={CreditCardIcon}
              label="Payment Method"
              onClick={() => nav('payment-method')}
            />

            <div style={{ borderTop: '1px solid var(--glass-border)', margin: '4px 0' }} />

            <DropdownItem
              icon={SettingsIcon}
              label="Settings"
              onClick={() => nav('user-settings')}
            />

            <DropdownItem
              icon={SlidersIcon}
              label="Preferences"
              onClick={() => nav('preferences')}
            />

            <div style={{ borderTop: '1px solid var(--glass-border)', margin: '4px 0' }} />

            <DropdownItem
              icon={LogOutIcon}
              label="Sign Out"
              danger
              onClick={() => { setOpen(false); onLogout(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownSection({ label, children }) {
  return (
    <div>
      <div className="uppercase tracking-wider" style={{ padding: '8px 12px 4px', fontSize: 10, fontWeight: 600, color: 'var(--text-3)' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function DropdownItem({ icon, label, badge, indent, danger, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3"
      style={{ padding: '8px 12px', paddingLeft: indent ? 36 : 12, fontSize: 14, color: danger ? 'var(--danger)' : 'var(--text-2)' }}
    >
      <span className="flex-shrink-0" style={{ width: 16, height: 16 }}>{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {badge && (
        <Badge tone="amber" dot={false}>{badge}</Badge>
      )}
    </button>
  );
}

const UserIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ShieldIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const KeyIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const CreditCardIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const SettingsIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SlidersIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

const LogOutIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
